<template>
  <div>
    <loadingCube v-if="loading"/>

    <div v-else class="main">
      <AdminModeContext v-slot="{ adminActive, adminBusy, toggleAdmin, withAdminLock }">
        <PageHeading v-if="site"
            :back="{ name: 'a_timesheet_r2_select_sites', params: { company_id, start_date, end_date } }"
        >
          <template #title>
            <router-link :to="{name: 'company_record', query: {company_id}}">
              {{ company?.company_name }}
            </router-link>
            <br/>
            <router-link :to="{ name: 'a_timesheet_r2_select_sites', params: { company_id, start_date, end_date } }">
              Submit Sites
            </router-link>
            -> Submit Timesheets
          </template>
          <template>
            <h2>{{ site.address }}</h2>
            <p>
              Selected Timesheet period: {{ dateFormat(start_date) }} - {{ dateFormat(end_date) }}.
              <router-link :to="{ name: 'a_timesheet_r2_select_period', params: { company_id } }">(change)</router-link>
            </p>
          </template>
        </PageHeading>

        <div v-else>
          Failed to get site information
        </div>

        <div class="pane preview_sites">
          <div>
            <h2>Assign worker</h2>
            <p>
              If one of your workers assigned to another site is missing from this site's
              timesheet, use this button to assign them to this
              location.<br/>On making this change the current state of the timesheet will be saved and this page will reload.
            </p>
          </div>
          <div class="preview_button">
            <button
                class="btn orcaBtn"
                :disabled="adminActive || saving || loading"
                style="max-width: 170px;"
                @click="goAssignWorker">
              <i class="fas fa-plus"></i> Assign Worker
            </button>
          </div>
        </div>

        <div class="content pane">
          <div style="display:grid;grid-template-columns:100%;grid-row-gap:1em;">
            <div style="display:grid;grid-template-columns:auto 250px 300px 170px;margin-bottom:10px;align-items:center;grid-template-rows: 40px;grid-column-gap: 1em;">
              <h2>Assigned Workers</h2>
              <h3>
                {{ items.length }} Workers <span v-if="search">({{ filteredItems.length }} filtered)</span>
              </h3>
              <input
                  :disabled="adminActive"
                  v-model="search"
                  style="width: 100%; height: 40px;border-radius: 4.75px;"
                  placeholder="Search by name…"
                  type="text"
              />
              <AdminToggleButton
                  :active="adminActive"
                  :toggle-admin="toggleAdmin"
              />
            </div>

            <!-- Site level admin actions, mirroring the select sites page. Both render as Force
                 variants for holders of sunset_timesheet_force_actions, soft for everyone else. -->
            <div class="header-panel" v-if="adminActive && site && isGod()">
              <label class="filter-label">Admin actions</label>
              <div class="filter-panel__admin-actions">
                <AdminReopenSiteButton
                    :company-id="company_id"
                    :site-id="site.site_id"
                    :period="period"
                    :admin-busy="adminBusy"
                    :with-admin-lock="withAdminLock"
                    :success="init"
                    v-if="['completed', 'paid'].includes(site.min_status_name)"
                />
                <AdminResetSiteButton
                    :company-id="company_id"
                    :site-id="site.site_id"
                    :period="period"
                    :admin-busy="adminBusy"
                    :with-admin-lock="withAdminLock"
                    :success="init"
                />
              </div>
            </div>
          </div>

          <ul class="worker-list">
            <WorkerListItem
                v-for="item in filteredItems"
                :key="`nl-${item.timesheet_id}`"
                :item="item"
                :order_id="order_id"
                :period="period"
                prefix="a"
            />
          </ul>

          <validation-observer ref="observer" v-slot="{ validate }">
            <form style="overflow-x: scroll;display: grid;" @submit.prevent="submitForm($event, validate)">
              <table id="timesheet_grid_table">
                <TableHeader
                    v-model="search"
                    :end_date="end_date"
                    :start_date="start_date"
                />
                <tbody>
                <template v-for="(item, index) in filteredItems">
                  <WorkerRowActive
                      v-if="selected_timesheet && getTsId(selected_timesheet) === item.timesheet_id && !item.submitted"
                      :company_id="company_id"
                      :key="`row-active-${item.timesheet_id}`"
                      :apply-geo-to-payments="applyGeoToPayments"
                      :end_date="end_date"
                      :handle-save="justSave"
                      :handle-finalise="handleFinalise"
                      :index="index"
                      :item="selected_timesheet"
                      :loading="loading"
                      :saving="saving"
                      :set-worker="setWorker"
                      :start_date="start_date"
                      :total-additions-deductions="totalAdditionsDeductions"
                      :total-price-for-hours="totalPriceForHours"
                      :worker="item"
                      :admin-active="adminActive"
                      :admin-busy="adminBusy"
                      :with-admin-lock="withAdminLock"
                      :on-refresh="fetchTimesheet"
                      @save-tab="handleSaveTab"
                      @save="justSave"
                  />
                  <WorkerRowInactive
                      v-else
                      :key="`row-inactive-${item.timesheet_id}`"
                      :company_id="company_id"
                      :apply-geo-to-payments="applyGeoToPayments"
                      :end_date="end_date"
                      :handle-finalise="handleFinalise"
                      :index="index"
                      :item="item"
                      :loading="loading"
                      :saving="saving"
                      :set-worker="setWorker"
                      :start_date="start_date"
                      :total-additions-deductions="totalAdditionsDeductions"
                      :total-price-for-hours="totalPriceForHours"
                      :admin-active="adminActive"
                      :admin-busy="adminBusy"
                      :with-admin-lock="withAdminLock"
                      :on-refresh="fetchTimesheet"
                      @save-tab="handleSaveTab"
                  />
                </template>
                </tbody>
              </table>
            </form>
          </validation-observer>
        </div>

        <div class="pane">
          <div>
            <button
                v-if="site && !site.completed && !isForeman()"
                :disabled="adminActive || saving || loading"
                class="btn orcaBtn"
                tabIndex="-1"
                @click="submitAllForSite">
              <span>Submit and Lock all</span>
            </button>

            <button
                v-else
                :disabled="adminActive || saving || loading"
                class="btn orcaBtn"
                tabIndex="-1"
                @click="goBackToSites">
              <span>Back</span>
            </button>
          </div>
        </div>
      </AdminModeContext>
    </div>
  </div>
</template>

<script>
import PageHeading from '../components/shared/PageHeading.vue';
import loadingCube from 'loaders/record_loader.vue';
import timesheet_form_container from './components/timesheet_form_container.vue';
import PaydayInputHorizontal from '../components/timesheet_edit_timesheet/payday_input_horizontal.vue';
import checkbox_input from '../components/timesheet_edit_timesheet/toggle_checkbox.vue';
import WorkerListItem from '../components/timesheet_site_workers/WorkerListItem.vue';
import {dateFormat, isForeman, isGod, showAdminDetails} from '../components/shared';
import WorkerRowActive from './components/WorkerRowActive.vue';
import WorkerRowInactive from './components/WorkerRowInactive.vue';
import TableHeader from '../components/timesheet_site_workers/TableHeader.vue';
import AdminModeContext from "pages/sunset/timesheets/components/shared/AdminModeContext.vue";
import AdminToggleButton from "pages/sunset/timesheets/components/shared/AdminToggleButton.vue";
import AdminResetSiteButton from "pages/sunset/timesheets/admin/components/AdminResetSiteButton.vue";
import AdminReopenSiteButton from "pages/sunset/timesheets/admin/components/AdminReopenSiteButton.vue";

export default {
  name: 'TimesheetEditWorkerTS',
  components: {
    AdminResetSiteButton,
    AdminReopenSiteButton,
    AdminToggleButton,
    AdminModeContext,
    PageHeading,
    TableHeader,
    WorkerRowInactive,
    WorkerRowActive,
    WorkerListItem,
    checkbox_input,
    payday_input_horizontal: PaydayInputHorizontal,
    timesheet_form_container,
    loadingCube
  },
  props: {
    company_id: {required: true},
    start_date: {required: true},
    end_date: {required: true},
    order_id: {required: true},
    order_job_id: {required: true}
  },
  data() {
    return {
      bypassNextRouteSave: false,
      saving: false,
      loading: false,
      site: null,
      period: this.end_date,
      selected_worker: null,
      selected_timesheet: null,
      company: null,
      items: [],
      search: '',
      url_params: {},
      shiftTimes: [],
      currentIndex: null,
      previousIndex: null,
      nextIndex: null,
      lastTabTime: null,
      singleTabTimer: null
    };
  },
  computed: {
    totalAdditionsDeductions() {
      if (this.selected_timesheet) {
        return this.getTotalAdditionsDeductions(this.selected_timesheet.additions_deductions);
      }
      return null;
    },
    totalPriceForHours() {
      if (this.selected_timesheet) {
        return this.getPriceForAmounts(this.selected_timesheet.payments, this.selected_timesheet.rate.pay_amount);
      }
      return null;
    },
    filteredItems() {
      const q = this.search.toLowerCase().trim();
      const base = this.items.filter(i => i && this.getTsId(i));
      if (!q) return base.slice();

      return base.filter(item => {
        const fullName = `${item.first_name} ${item.surname} ${item.job_title} ${item.id} ${item.worker_id} ${item.timesheet_id}`.toLowerCase();
        return fullName.includes(q);
      });
    }
  },
  watch: {
    filteredItems() {
      if (!this.selected_worker) return;

      const tsId = this.getTsId(this.selected_worker);
      const idx = this.indexOfItem(tsId);

      this.currentIndex = idx;
      this.previousIndex = idx > 0 ? idx - 1 : null;
      this.nextIndex = idx !== -1 && idx < this.filteredItems.length - 1 ? idx + 1 : null;
    },
    selected_timesheet: {
      deep: true,
      handler() {
        this.syncSelectedIntoItems();
      }
    }
  },
  async beforeMount() {
    await this.init();
  },
  async beforeRouteLeave(to, from, next) {
    if (this.bypassNextRouteSave) {
      this.bypassNextRouteSave = false;
      next();
      return;
    }

    const ok = await this.saveCurrentTimesheetBeforeLeaving(false);
    next(ok ? undefined : false);
  },
  methods: {
    async goAssignWorker() {
      const saved = await this.saveCurrentTimesheetBeforeLeaving(false);
      if (!saved) return;

      this.bypassNextRouteSave = true;

      try {
        await this.$router.push({
          name: 'a_timesheet_r2_assign_worker',
          params: {
            company_id: this.company_id,
            start_date: this.start_date,
            end_date: this.end_date,
            order_id: this.order_id,
            order_job_id: this.order_job_id
          }
        });
      } catch (err) {
        this.bypassNextRouteSave = false;
        throw err;
      }
    },

    async goBackToSites() {
      const saved = await this.saveCurrentTimesheetBeforeLeaving(false);
      if (!saved) return;

      this.bypassNextRouteSave = true;

      try {
        await this.$router.push({
          name: 'a_timesheet_r2_select_sites',
          params: {
            company_id: this.company_id,
            start_date: this.start_date,
            end_date: this.end_date
          }
        });
      } catch (err) {
        this.bypassNextRouteSave = false;
        throw err;
      }
    },

    async flushActiveInput() {
      const active = document.activeElement;

      if (active && this.$el.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }

      await this.$nextTick();
      await this.$nextTick();
    },

    async saveCurrentTimesheetBeforeLeaving(notify = false) {
      if (!this.selected_timesheet || this.selected_timesheet.submitted !== false) {
        return true;
      }

      if (this.saving || this.loading) {
        return false;
      }

      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      return await this.submitForm(
          {submitter: {name: 'save_for_later'}},
          validateFn,
          notify,
          true,
          true
      );
    },

    async submitCurrentTimesheetBeforeSiteSubmit() {
      if (!this.selected_timesheet || this.selected_timesheet.submitted !== false) {
        return true;
      }

      if (this.saving || this.loading) {
        return false;
      }

      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      return await this.submitForm(
          {submitter: {name: 'finalise'}},
          validateFn,
          false,
          false,
          true
      );
    },

    async init() {
      this.loading = true;
      this.url_params.order_id = parseInt(this.order_id);
      this.url_params.order_job_id = parseInt(this.order_job_id);

      await this.fetchSite();
      await this.fetchSiteWorkers();
      await this.fetchSupportingData();

      const timesheetId = parseInt(this.$route.query.timesheet);
      let selected = this.items.find(item => this.getTsId(item) === timesheetId);

      if (!selected) {
        const workerId = parseInt(this.$route.query.worker);

        if (workerId) {
          selected = this.items.find(item => item.worker_id === workerId);
        }
      }

      if (!selected && this.items.length > 0) {
        selected = this.items[0];
        this.updateURLWithTimesheetId(this.getTsId(selected));
      }

      if (selected) {
        const index = this.indexOfItem(this.getTsId(selected));

        this.selected_worker = selected;
        this.currentIndex = index;
        this.previousIndex = index > 0 ? index - 1 : null;
        this.nextIndex = index < this.filteredItems.length - 1 ? index + 1 : null;

        await this.fetchTimesheet(this.getTsId(selected));
      }

      this.loading = false;
    },

    isForeman,

    isGod,

    getTsId(obj) {
      return obj ? (obj.timesheet_id || obj.id) : null;
    },

    indexOfItem(timesheetId) {
      return this.filteredItems.findIndex(i => this.getTsId(i) === timesheetId);
    },

    syncSelectedIntoItems() {
      const ts = this.selected_timesheet;
      const tsId = this.getTsId(ts);

      if (!ts || !tsId) return;

      const idx = this.items.findIndex(i => this.getTsId(i) === tsId);

      if (idx === -1) return;

      const totalAdditionsDeductions = this.getTotalAdditionsDeductions(ts.additions_deductions || []);
      const totalPriceForHours = ts.rate ? this.getPriceForAmounts(ts.payments || [], ts.rate.pay_amount) : 0;

      this.$set(this.items, idx, {
        ...this.items[idx],
        additions_deductions: Array.isArray(ts.additions_deductions) ? [...ts.additions_deductions] : [],
        payments: Array.isArray(ts.payments) ? [...ts.payments] : [],
        rate: ts.rate ? {...ts.rate} : null,
        status_code: ts.status_code,
        submitted: ts.submitted,
        status_name: ts.status_name,
        totalAdditionsDeductions,
        totalPriceForHours
      });
    },

    async fetchSupportingData() {
      const result = await axios.get(this.crmApiPath + 'company/' + this.company_id);

      if (result.status !== 200) {
        console.error('Could not fetch company');
        return;
      }

      this.company = result.data;
    },

    async submitAllForSite() {
      const submittedCurrent = await this.submitCurrentTimesheetBeforeSiteSubmit();
      if (!submittedCurrent) return;

      this.saving = true;

      try {
        const result = await axios.post(
            `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/submit_whole_site/${this.order_job_id}?submission_date=${this.period}`
        );

        if (result.status !== 200) {
          console.error('Could not submit site');
          return;
        }

        await this.fetchSite();
        await this.fetchSiteWorkers();

        if (this.selected_timesheet) {
          this.selected_timesheet = {
            ...this.selected_timesheet,
            submitted: true
          };

          this.syncSelectedIntoItems();
        }
      } finally {
        this.saving = false;
      }
    },

    async handleFinalise(e) {
      e.preventDefault();

      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      const saved = await this.submitForm(
          {submitter: {name: 'finalise'}},
          validateFn,
          true
      );

      if (!saved) return;

      await this.nextWorker();
    },

    updateURLWithTimesheetId(timesheetId) {
      if (window.history.replaceState) {
        const newURL = `${window.location.origin}${window.location.pathname}?timesheet=${timesheetId}`;
        window.history.replaceState({}, '', newURL);
      }
    },

    getTotalAdditionsDeductions(additions_deductions) {
      return additions_deductions.reduce((total, item) => {
        return total + (item.add_ded_type_id === 30 || item.add_ded_type_id === 32 ? -item.amount : item.amount);
      }, 0);
    },

    getPriceForAmounts(payments, rate) {
      return payments.reduce((total, item) => {
        return total + (item.absence_type == null && item.amount > 0 ? item.amount * rate : 0);
      }, 0);
    },

    async applyGeoToPayments(timesheet) {
      if (!Array.isArray(timesheet.geo) || !Array.isArray(timesheet.payments)) {
        return timesheet;
      }

      timesheet.payments.forEach((payment, index) => {
        if (timesheet.payments[index]) {
          this.$set(timesheet.payments, index, {
            ...timesheet.payments[index],
            amount: timesheet.geo[index] ?? 0
          });
        }
      });

      this.selected_timesheet = {...timesheet};

      const saved = await this.saveSelectedWorker(true);

      if (!saved) return timesheet;

      this.syncSelectedIntoItems();

      return timesheet;
    },

    async saveSelectedWorker(notify = false) {
      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      return await this.submitForm(
          {submitter: {name: 'save_for_later'}},
          validateFn,
          notify
      );
    },

    dateFormat,

    async fetchSiteWorkers() {
      const result = await axios.get(
          `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/order_job/${this.order_job_id}/workers_detailed?submission_date=${this.period}`
      );

      if (result.status !== 200) {
        console.error('Could not fetch worker data');
        return;
      }

      this.items = result.data.data.map(item => {
        const totalAdditionsDeductions = this.getTotalAdditionsDeductions(item.additions_deductions || []);
        const totalPriceForHours = item.rate ? this.getPriceForAmounts(item.payments || [], item.rate.pay_amount) : 0;

        return {
          ...item,
          totalAdditionsDeductions,
          totalPriceForHours
        };
      });
    },

    async fetchSite() {
      const result = await axios.get(
          `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/sites/${this.order_id}/${this.order_job_id}?submission_date=${this.period}`
      );

      if (result.status !== 200) {
        console.error('Could not fetch site');
        return;
      }

      this.site = result.data.data;
    },

    async updateCurrentTimesheet(data) {
      const tsId = this.getTsId(data);

      this.selected_timesheet = {...data};

      const idx = this.items.findIndex(i => this.getTsId(i) === tsId);

      if (idx !== -1) {
        this.$set(this.items, idx, {
          ...this.items[idx],
          additions_deductions: Array.isArray(data.additions_deductions) ? [...data.additions_deductions] : [],
          payments: Array.isArray(data.payments) ? [...data.payments] : [],
          rate: data.rate ? {...data.rate} : null,
          submitted: data.submitted,
          total_gross: data.total_gross,
          totalAdditionsDeductions: this.getTotalAdditionsDeductions(data.additions_deductions || []),
          totalPriceForHours: data.rate ? this.getPriceForAmounts(data.payments || [], data.rate.pay_amount) : 0
        });
      }
    },

    async fetchTimesheet(timesheetId) {
      const result = await axios.get(
          `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/timesheets/${timesheetId}`
      );

      if (result.status !== 200) {
        console.error('Could not fetch timesheet:', result);
        this.error = 'Failed to load timesheet data';
        return;
      }

      await this.updateCurrentTimesheet(result.data.data);
    },

    async setWorker(index, skipSave = false) {
      const nextItem = this.filteredItems[index];
      const nextTsId = this.getTsId(nextItem);

      if (this.selected_timesheet && nextTsId === this.getTsId(this.selected_timesheet)) return;
      if (this.saving || this.loading) return;

      if (!skipSave && this.selected_timesheet?.submitted === false) {
        const saved = await this.saveSelectedWorker(false);

        if (!saved) return;
      }

      this.syncSelectedIntoItems();

      this.currentIndex = index;
      this.previousIndex = index > 0 ? index - 1 : null;
      this.nextIndex = index < this.filteredItems.length - 1 ? index + 1 : null;

      this.selected_timesheet = null;

      if (nextTsId) {
        await this.fetchTimesheet(nextTsId);
      }

      this.selected_worker = nextItem;

      if (this.selected_worker) {
        this.updateURLWithTimesheetId(nextTsId);
      }

      this.$nextTick(() => {
        const firstInput = this.$el.querySelector('.day_col input');

        if (firstInput) {
          firstInput.focus();
          firstInput.select();
        }
      });
    },

    async submitForm(event, validateFn, notify, updateLocal = true, allowSubmittedConflict = false) {
      notify = notify === true;
      this.saving = true;

      const timesheetId = this.getTsId(this.selected_timesheet);

      if (!timesheetId) {
        console.info('No timesheet selected');
        this.saving = false;
        return true;
      }

      const isValid = await validateFn();

      if (!isValid) {
        alert('Please complete all required fields.');
        this.saving = false;
        return false;
      }

      const isFinalised = event.submitter?.name === 'finalise';

      const payments = (this.selected_timesheet.payments || []).map(item => ({
        id: item.id,
        amount: item.amount,
        absence_type: item.absence_type
      }));

      const payload = {
        retention_released: this.selected_timesheet.retention_released,
        back_next_week: this.selected_timesheet.back_next_week,
        submit: isFinalised,
        additions_deductions: this.selected_timesheet.additions_deductions,
        payments
      };

      try {
        const updated = await axios.post(
            `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/timesheets/${timesheetId}`,
            payload
        );

        if (updated.status === 200) {
          if (updateLocal) {
            await this.updateCurrentTimesheet(updated.data.data);
            this.syncSelectedIntoItems();
          }

          if (notify && this.selected_worker) {
            this.$snotify.success(
                `${!isFinalised ? 'Saved' : 'Finalised'} ${this.selected_worker.first_name + ' ' + this.selected_worker.surname}`,
                {
                  timeout: 1500,
                  closeOnClick: true
                }
            );
          }

          return true;
        }

        if (updated.status === 409) {
          if (allowSubmittedConflict) {
            console.info('Timesheet already submitted during flush', payload);
            return true;
          }

          return false;
        }

        return false;
      } catch (err) {
        if (
            allowSubmittedConflict &&
            err?.response?.status === 409 &&
            String(err?.response?.data?.error || '').includes('already been submitted')
        ) {
          return true;
        }

        console.warn('Save timesheet failed', payload, err);

        if (notify && this.selected_worker) {
          this.$snotify.error(
              `${!isFinalised ? 'Save' : 'Finalise'} ${this.selected_worker.first_name + ' ' + this.selected_worker.surname} failed - validation failure - check values.`,
              {
                timeout: 1500,
                closeOnClick: true
              }
          );
        }

        return false;
      } finally {
        this.saving = false;
      }
    },

    async justSave(e) {
      e.preventDefault();

      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      await this.submitForm(
          {submitter: {name: 'save_for_later'}},
          validateFn,
          true
      );
    },

    async handleSaveTab(e) {
      e.preventDefault();

      await this.flushActiveInput();

      const observer = this.$refs.observer;
      const validateFn = observer?.validate || (() => Promise.resolve(true));

      const saved = await this.submitForm(
          {submitter: {name: 'save_for_later'}},
          validateFn,
          true
      );

      if (!saved) return;

      await this.nextWorker(true);
    },

    async nextWorker(skipSave = false) {
      const currentTsId = this.getTsId(this.selected_timesheet || this.selected_worker);
      const currentIndex = this.filteredItems.findIndex(w => this.getTsId(w) === currentTsId);

      let nextIndex = currentIndex + 1;

      while (nextIndex < this.filteredItems.length && this.filteredItems[nextIndex].submitted) {
        nextIndex++;
      }

      if (nextIndex < this.filteredItems.length) {
        await this.setWorker(nextIndex, skipSave);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* Admin actions panel - matches the equivalent panel on the select sites page. */
.header-panel {
  display: grid;
  min-width: 400px;
  grid-template-columns: 100%;
  grid-template-rows: 35px 1fr;
  justify-content: flex-start;
}

.filter-panel__admin-actions {
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 10px;
  grid-auto-columns: max-content;
  justify-content: flex-start;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #777;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

div.preview_sites {
  display: grid;
  padding: 1em;
  grid-template-columns: 2fr 1fr;

  .preview_button {
    display: grid;
    justify-content: flex-end;
    align-content: center;

    button {
      width: 170px;
      min-width: 170px;
    }
  }

  margin-bottom: 1em;
}

.content {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 100%;
  align-content: center;
  justify-content: center;
}

ul.worker-list {
  display: block;
  min-width: 250px;
  font-size: 16px;
  font-weight: bold;
  margin: 0;
  padding: 0;
}

.page_heading {
  padding: 1em;
  margin-bottom: 1em;
}

.pane {
  padding: 1em;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 10px;
}

#timesheet_grid_table tr:nth-child(2n + 1) {
  background-color: #f8f8f8;
}

#timesheet_grid_table tr.selected {
  background-color: #cecece;
}

#timesheet_grid_table tr {
  height: 30px;
}

#timesheet_grid_table td,
#timesheet_grid_table th {
  height: 100%;
}

#timesheet_grid_table {
  display: none;
  min-width: 1300px;
  table-layout: fixed;
  overflow-x: auto;
}

$screen-xl-min: 1400px;

@media (min-width: #{$screen-xl-min}) {
  ul.worker-list {
    display: none;
  }

  #timesheet_grid_table {
    display: table;
  }
}
</style>